import {
  CreateSecretCommand,
  GetSecretValueCommand,
  SecretsManagerClient,
  UpdateSecretCommand,
} from "@aws-sdk/client-secrets-manager";

import { Environment, isObject, isString } from "@abacus/common";

import { SecretsManagerOptions } from "./types";

export class SecretsManager {
  client: SecretsManagerClient;
  environment: Environment;
  forceRemote: boolean;
  localToRemoteNames?: Record<string, string>;
  serviceName: string;

  constructor({
    client,
    environment,
    forceRemote,
    localToRemoteNames,
    serviceName,
  }: SecretsManagerOptions) {
    this.client = client;
    this.environment = environment;
    this.forceRemote = forceRemote ?? false;
    this.localToRemoteNames = localToRemoteNames;
    this.serviceName = serviceName;
  }

  async getCred(
    envVar: string,
  ): Promise<string | Uint8Array | unknown | undefined> {
    if (
      !this.forceRemote &&
      (this.environment == Environment.Dev ||
        this.environment == Environment.Test)
    ) {
      return this.tryParseJson(process.env[envVar] ?? "undefined");
    }

    envVar = this.localToRemoteNames?.[envVar] ?? envVar;
    const secretName = `${this.environment}/${this.serviceName}/${envVar}`;
    const secret = await this.getSecret(secretName);

    if (isString(secret)) {
      return secret;
    }

    if (isObject(secret) && secretName in secret) {
      return (secret as Record<string, string>)[secretName];
    }

    return secret;
  }

  async getSecret(
    secretName: string,
  ): Promise<string | Uint8Array | unknown | undefined> {
    const response = await this.client.send(
      new GetSecretValueCommand({
        SecretId: secretName,
      }),
    );

    if (response.SecretString) {
      return this.tryParseJson(response.SecretString);
    }

    if (response.SecretBinary) {
      return response.SecretBinary;
    }

    return undefined;
  }

  async storeCred(key: string, secret: unknown): Promise<void> {
    if (
      this.environment == Environment.Dev ||
      this.environment == Environment.Test
    ) {
      process.env[key] = JSON.stringify(secret);
    }

    const secretName = `${this.environment}/${this.serviceName}/${key}`;
    const secretStr = JSON.stringify(secret);
    await this.client.send(
      new CreateSecretCommand({
        Name: secretName,
        SecretString: secretStr,
      }),
    );
  }

  async updateSecret(key: string, secret: unknown): Promise<void> {
    if (this.environment == Environment.Test) {
      process.env[key] = JSON.stringify(secret);
      return;
    }

    const secretName = `${this.environment}/${this.serviceName}/${key}`;
    await this.client.send(
      new UpdateSecretCommand({
        SecretId: secretName,
        SecretString: "" + secret,
      }),
    );
  }

  private tryParseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch (_) {
      // pass
    }
    return value;
  }
}
